import styled from "@emotion/styled";
import type { UnsettledBalance } from "@stepfinance/dex";
import type { StepColumnsType } from "@stepfinance/react";
import { ContextButton, formatBalance, getColumnSearchProps, StepTable, TokenIconText } from "@stepfinance/react";
import type { TablePaginationConfig } from "antd";
import type { FC } from "react";
import React, { useEffect, useMemo, useState } from "react";

import { useEnrichedWallet } from "../../../../contexts/enrichedWallet";
import { useLanguageContext } from "../../../../contexts/language/language";
import { useSettleOpenbookFunds } from "../../../../hooks/mutations/openbook/useSettleOpenbookFunds";
import type { UnsettledBalanceRow } from "../../../../types/dex";
import { getTokenByMint } from "../../../../utils/tokenInfo";
import { isPaginationHidden, useLocalStorageState } from "../../../../utils/utils";
import RedirectLink from "../../../RedirectLink/RedirectLink";
import { PAGE_SIZE_OPTIONS } from "../../../shared";
import CellValue from "../../../Table/CellValue";
import { TokenIcon } from "../../../TokenIcon";
import { ChevronCollapse } from "../../ChevronCollapse/ChevronCollapse";

interface UnsettledBalancesTableProps {
  unsettledBalanceList: UnsettledBalance[];
  unsettledBalancesValue: number | null;
}

export const UnsettledBalancesTable: FC<UnsettledBalancesTableProps> = ({
  unsettledBalanceList,
  unsettledBalancesValue,
}) => {
  const { watching } = useEnrichedWallet();
  const settleFundsMutation = useSettleOpenbookFunds();

  const [pagination, setPagination] = useState<TablePaginationConfig>({});
  const [paginationSetting, setPaginationSetting] = useLocalStorageState(
    "unsettledBalancePaginationSizeSetting",
    PAGE_SIZE_OPTIONS.m[0],
  );

  const {
    currentLanguageData: {
      common,
      components: {
        table: { headers },
      },
      pages: {
        dashboard: {
          modules: { DEXModule },
        },
      },
    },
  } = useLanguageContext();

  useEffect(() => {
    setPagination({
      hideOnSinglePage: isPaginationHidden(unsettledBalanceList.length, PAGE_SIZE_OPTIONS.m),
      showSizeChanger: true,
      pageSizeOptions: PAGE_SIZE_OPTIONS.m,
      pageSize: parseInt(paginationSetting),
    });
  }, [unsettledBalanceList.length, paginationSetting]);

  // There's one openOrders acct for each pair of unsettled balances.
  // For txns and everything else it makes sense to have a single object with both balances.
  // In the table we display each token unsettled balance as a separate row.
  // So we only separate them in this component specifically.
  const unsettledBalanceRows = useMemo(() => {
    const rows: UnsettledBalanceRow[] = [];
    unsettledBalanceList.forEach((ub) => {
      [ub.baseAmountFree, ub.quoteAmountFree].forEach((amount, index) => {
        if (amount) {
          const isBaseToken = index === 0;
          const priceInUSD = isBaseToken ? ub.basePriceUSD ?? 0 : ub.quotePriceUSD ?? 0;
          rows.push({
            marketName: ub.marketName,
            marketAddress: ub.marketAddress,
            baseMint: ub.baseMint,
            quoteMint: ub.quoteMint,
            isBaseToken,
            amount,
            priceInUSD,
            valueInUSD: (priceInUSD ?? 0) * amount,
          });
        }
      });
    });
    return rows;
  }, [unsettledBalanceList]);

  const unsettledBalancesColumns: StepColumnsType<UnsettledBalanceRow> = [
    {
      title: common.market,
      dataIndex: "marketName",
      key: "marketName",
      render: (_, record) => {
        return (
          <MarketContainer>
            <TokenIcon mintAddress={[record.baseMint, record.quoteMint]} />
            <TokenIconText>
              <RedirectLink href={`https://dex.solape.io/#/market/${record.marketAddress.toBase58()}`}>
                {record.marketName}
              </RedirectLink>
            </TokenIconText>
          </MarketContainer>
        );
      },
      sorter: (a, b) => {
        return a.marketName.localeCompare(b.marketName);
      },
      ...getColumnSearchProps((record) => record.marketName, "market"),
    },
    {
      title: common.amount,
      dataIndex: "amount",
      key: "amount",
      className: "column-number",
      sorter: (a, b) => b.amount - a.amount,
      showSorterTooltip: false,
      render(_, record) {
        const mint = record.isBaseToken ? record.baseMint : record.quoteMint;
        return <CellValue cellType="number">{formatBalance(record.amount, getTokenByMint(mint)?.decimals)}</CellValue>;
      },
    },
    {
      title: headers.price,
      dataIndex: "priceInUSD",
      key: "priceInUSD",
      className: "column-number",
      sorter: (a, b) => b.priceInUSD - a.priceInUSD,
      showSorterTooltip: false,
      render(_, record) {
        return (
          <CellValue cellType="value" fontWeight="regular">
            {record.priceInUSD}
          </CellValue>
        );
      },
    },
    {
      title: () => (
        <>
          <ValueContainer>
            {!watching && (
              <ContextButton
                disabled={settleFundsMutation.isLoading}
                onClick={(event) => {
                  event.stopPropagation();
                  settleFundsMutation.mutate(unsettledBalanceList);
                }}
              >
                {DEXModule.buttons.settleAll}
              </ContextButton>
            )}
            <ValueTitle>{common.value}</ValueTitle>
          </ValueContainer>
        </>
      ),
      dataIndex: "valueInUSD",
      key: "valueInUSD",
      className: "column-number",
      sorter: (a, b) => a.valueInUSD - b.valueInUSD,
      showSorterTooltip: false,
      defaultSortOrder: "descend" as any,
      render(_, record) {
        const value = record.valueInUSD;
        return (
          <CellValue cellType="value" fontWeight="bold">
            {value}
          </CellValue>
        );
      },
    },
  ];

  return (
    <UnsettleBalancesContainer>
      <ChevronCollapse isCollapseChild title={DEXModule.unsettledBalances} value={unsettledBalancesValue}>
        <StepTable
          rowKey={(record, index) => `${index}-${record.amount}-${record.marketName}`}
          onChange={(pagination) => {
            setPaginationSetting(pagination.pageSize ? pagination.pageSize.toString() : PAGE_SIZE_OPTIONS.m[0]);
          }}
          pagination={pagination}
          columns={unsettledBalancesColumns}
          dataSource={unsettledBalanceRows}
        />
      </ChevronCollapse>
    </UnsettleBalancesContainer>
  );
};

const UnsettleBalancesContainer = styled.div`
  margin-top: 20px;
`;

const ValueTitle = styled.span`
  margin-left: 8px;
`;

const MarketContainer = styled.div`
  display: flex;
`;

const ValueContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
`;
